// Compass heading + tilt shim for the field workspaces (2026-10-02).
//
// iOS Safari: DeviceOrientationEvent.requestPermission() must be called from
// a user tap (we call it from "Start" / "Take photo"), then the non-standard
// event.webkitCompassHeading (degrees from magnetic north, already for the
// rear camera when the phone is upright) and webkitCompassAccuracy (± deg).
// Android Chrome: the `deviceorientationabsolute` event gives a
// north-referenced alpha; the camera direction is computed from
// alpha/beta/gamma (W3C DeviceOrientation spec example, mirrored and
// unit-tested in src/wcc_viewer/field_geo.py: compass_heading).
// The webkit heading is corrected for screen.orientation.angle (it follows
// the device's top edge); the absolute one needs no correction. Either way it is
// RECORDED AT CAPTURE, because iOS strips GPS (and most EXIF) from photos.
//
// The server converts nothing: it receives true-ish compass headings. The
// magnetic declination in Wellington is about +22-23°; webkitCompassHeading
// is magnetic, Android absolute alpha is usually true north. We apply a
// fixed declination to the webkit source (DECLINATION_DEG) so both are TRUE.

//
// 2026-10-05 (AR stability): the published heading is the compass FUSED
// WITH THE GYRO (arTrack.HeadingFilter: DeviceMotion rotationRate projected
// on the vertical, its gain/sign learned from the compass itself), so it no
// longer jitters, and a magnetic disturbance - a compass swing the gyro
// didn't see, or iOS webkitCompassAccuracy getting worse - is held off and
// reported (`disturbed`, `note`). DeviceMotion events are also handed to
// onMotion() listeners (track.ts counts steps from them).
//
// 2026-10-05 (iPhone: "the orientation goes all over the show when I angle
// the phone to look around"): the camera heading now comes from the FULL
// orientation (rotation matrix of alpha/beta/gamma: the azimuth of the
// camera's -z axis, right for any pitch and roll), and iOS
// webkitCompassHeading - which follows the device's TOP EDGE and is
// ill-defined when the phone is held up - only anchors north, in poses
// where top edge and camera agree. arTrack.OrientationFusion has the detail
// and frontend/tests/orientation.test.ts the upright / pan / tilt / roll
// sequences. The header above about the webkit heading being "already the
// rear camera when upright" turned out not to hold.
import { OrientationFusion } from "./arTrack";
import type { SensorDetails } from "./arTrack";

export type HeadingSource = "webkit" | "absolute" | "relative" | "none";

export interface HeadingReading {
  heading: number | null; // degrees clockwise from true north, camera direction
  accuracy: number | null; // ± degrees when the platform reports it
  source: HeadingSource;
  pitch: number | null; // camera pitch above the horizon (deg)
  roll: number | null;
  at: number;
  /** the compass looks magnetically disturbed (cars, steel): held on the gyro */
  disturbed?: boolean;
  note?: string | null;
  /** the unfused compass reading (as a camera heading) */
  raw?: number | null;
  /** for the AR view's "Sensor details" readout */
  details?: SensorDetails;
}

// Magnetic declination (magnetic -> true) for the iOS webkit heading. It
// varies across NZ: ~19.8 deg Auckland, 22.6 Wellington, 24.0 Christchurch,
// 25.4 Dunedin (NOAA WMM, 2026). A straight-line fit in latitude through
// those is within ~0.5 deg on the main centres - better than the old fixed
// Wellington value, which was 1.3 deg out at the Christchurch field test.
// The fix's latitude sets it (setDeclinationForLat); Wellington until then.
let DECLINATION_DEG = 22.6;

export function declinationForLat(lat: number): number {
  if (!Number.isFinite(lat) || lat > -33 || lat < -48) return 22.6;
  return 19.8 + 0.62 * (-36.85 - lat);
}

export function setDeclinationForLat(lat: number): void {
  DECLINATION_DEG = declinationForLat(lat);
}

export function norm360(d: number): number {
  const r = d % 360;
  return r < 0 ? r + 360 : r;
}

export function angleDiff(a: number, b: number): number {
  const d = norm360(a - b);
  return d > 180 ? d - 360 : d;
}

/** Camera (rear) heading from an absolute DeviceOrientation reading. */
export function compassHeading(alpha: number, beta: number, gamma: number): number | null {
  const r = Math.PI / 180;
  const ca = Math.cos(alpha * r), sa = Math.sin(alpha * r), sb = Math.sin(beta * r);
  const cg = Math.cos(gamma * r), sg = Math.sin(gamma * r);
  const ra = -ca * sg - sa * sb * cg;
  const rb = -sa * sg + ca * sb * cg;
  if (Math.abs(ra) < 1e-9 && Math.abs(rb) < 1e-9) return null;
  return norm360(Math.atan2(ra, rb) / r);
}

const COMPASS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
export function compassPoint(deg: number): string {
  return COMPASS[Math.floor((norm360(deg) + 22.5) / 45) % 8];
}

export function screenAngle(): number {
  const so = (screen as Screen & { orientation?: { angle: number } }).orientation;
  if (so && typeof so.angle === "number") return so.angle;
  const wo = (window as unknown as { orientation?: number }).orientation;
  return typeof wo === "number" ? wo : 0;
}

let current: HeadingReading = { heading: null, accuracy: null, source: "none", pitch: null, roll: null, at: 0 };
let started = false;
const fusion = new OrientationFusion();
let lastBeta: number | null = null;
let lastGamma: number | null = null;
let motionSeen = false;
const listeners = new Set<(r: HeadingReading) => void>();
const motionListeners = new Set<(e: DeviceMotionEvent) => void>();

/** Publish the fused reading. `beta` / `gamma`: the event's W3C tilt (the
 * overlay's cameraBasis takes them with the camera heading). */
function publish(source: HeadingSource, beta: number | null, gamma: number | null): void {
  if (beta !== null) lastBeta = beta;
  if (gamma !== null) lastGamma = gamma;
  const r = fusion.reading(performance.now() / 1000);
  current = {
    heading: r.heading === null ? null : Math.round(r.heading * 10) / 10,
    accuracy: r.accuracy,
    source: r.heading === null ? "none" : source,
    pitch: lastBeta === null ? null : Math.round((lastBeta - 90) * 10) / 10,
    roll: lastGamma === null ? null : Math.round(lastGamma * 10) / 10,
    at: Date.now(),
    disturbed: r.disturbed,
    note: r.note,
    raw: r.raw,
    details: r.details,
  };
  for (const fn of listeners) fn(current);
}

function onMotionEvent(e: Event): void {
  const ev = e as DeviceMotionEvent;
  motionSeen = true;
  const rr = ev.rotationRate;
  // only the no-attitude fallback uses the raw gyro: with an attitude stream
  // (iOS always; Android's relative event) the attitude carries the turns
  if (rr && rr.alpha !== null && rr.beta !== null && rr.gamma !== null && lastBeta !== null && lastGamma !== null) {
    fusion.gyro(performance.now() / 1000, rr.alpha, rr.beta, rr.gamma, lastBeta, lastGamma);
  }
  for (const fn of motionListeners) fn(ev);
}

/** DeviceMotion events (accelerometer + gyro), once startHeading() ran. */
export function onMotion(fn: (e: DeviceMotionEvent) => void): () => void {
  motionListeners.add(fn);
  return () => motionListeners.delete(fn);
}

/** Has any DeviceMotion event arrived (steps / gyro usable)? */
export function motionAvailable(): boolean {
  return motionSeen;
}

type IOSOrientationEvent = DeviceOrientationEvent & { webkitCompassHeading?: number; webkitCompassAccuracy?: number };

function onWebkit(e: Event): void {
  const ev = e as IOSOrientationEvent;
  const wk = typeof ev.webkitCompassHeading === "number" && !Number.isNaN(ev.webkitCompassHeading) ? ev.webkitCompassHeading : null;
  const acc = typeof ev.webkitCompassAccuracy === "number" ? ev.webkitCompassAccuracy : null;
  // the camera heading from the FULL orientation; webkitCompassHeading only
  // anchors north, and only in poses where it is trustworthy (arTrack.ts:
  // OrientationFusion explains why)
  fusion.webkit(performance.now() / 1000, ev.alpha, ev.beta, ev.gamma, wk, acc, DECLINATION_DEG, screenAngle());
  publish("webkit", ev.beta, ev.gamma);
}

function onAbsolute(e: Event): void {
  const ev = e as DeviceOrientationEvent;
  if (ev.alpha === null || ev.beta === null || ev.gamma === null) return;
  // the rear camera's azimuth from the full rotation matrix: right whatever
  // way the screen is turned, so no screen-angle term (fixed 2026-10-02:
  // field_ar.py camera_basis landscape == portrait)
  fusion.absolute(performance.now() / 1000, ev.alpha, ev.beta, ev.gamma, screenAngle());
  publish("absolute", ev.beta, ev.gamma);
}

function onRelative(e: Event): void {
  const ev = e as DeviceOrientationEvent;
  if (ev.alpha === null || ev.beta === null || ev.gamma === null) return;
  // Android's relative (gyro-based) attitude: carries the turns between
  // absolute readings
  fusion.relative(performance.now() / 1000, ev.alpha, ev.beta, ev.gamma, screenAngle());
  publish("absolute", ev.beta, ev.gamma);
}

/** Must be called from a user gesture on iOS. Resolves to false when the
 * user refused or the device has no compass. Safe to call repeatedly. */
export async function startHeading(): Promise<boolean> {
  if (started) return true;
  const DOE = (window as unknown as { DeviceOrientationEvent?: { requestPermission?: () => Promise<string> } })
    .DeviceOrientationEvent;
  if (!DOE) return false;
  // iOS Safari needs requestPermission() from the tap. Current Chromium
  // (so Android Chrome too) ALSO exposes requestPermission now, so its
  // presence no longer means "iOS": ask if it's there, then listen by
  // capability rather than by browser. Previously the requestPermission
  // branch only listened for iOS's webkitCompassHeading and returned, so
  // Android never got a compass.
  if (typeof DOE.requestPermission === "function") {
    try {
      const res = await DOE.requestPermission();
      if (res !== "granted") return false;
    } catch {
      return false;
    }
  }
  // DeviceMotion (gyro for the heading, accelerometer for steps). On iOS it
  // shares the "Motion & Orientation" permission just granted; asking again
  // resolves without a second prompt. Optional: without it the heading is
  // plain low-passed compass and position is GPS only.
  const DME = (window as unknown as { DeviceMotionEvent?: { requestPermission?: () => Promise<string> } }).DeviceMotionEvent;
  if (DME) {
    let ok = true;
    if (typeof DME.requestPermission === "function") {
      try {
        ok = (await DME.requestPermission()) === "granted";
      } catch {
        ok = false;
      }
    }
    if (ok) window.addEventListener("devicemotion", onMotionEvent);
  }
  const hasAbsolute = "ondeviceorientationabsolute" in (window as object);
  if (hasAbsolute) window.addEventListener("deviceorientationabsolute", onAbsolute);
  // iOS: webkitCompassHeading on the plain event. Elsewhere (no absolute
  // event) some browsers deliver absolute readings on the plain event.
  (window as Window).addEventListener("deviceorientation", (e: Event) => {
    const ev = e as IOSOrientationEvent;
    if (typeof ev.webkitCompassHeading === "number" || "webkitCompassHeading" in ev) onWebkit(e);
    else if (ev.absolute) {
      if (!hasAbsolute) onAbsolute(e);
    } else onRelative(e);
  });
  started = true;
  return true;
}

export function getHeading(): HeadingReading {
  // a reading older than 3 s is stale (sensor paused, tab backgrounded)
  if (current.at && Date.now() - current.at > 3000) return { ...current, heading: null, source: "none" };
  return current;
}

export function onHeading(fn: (r: HeadingReading) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
